import type {
  ConnectDiagnosticsReport,
  LatencyEndpointResult,
  LatencyTier,
  MediaEndpointResult,
} from './types'

/** Exact Sentry Logs `message` values emitted by Connect diagnostics. */
export const DIAGNOSTICS_LOG_MESSAGES = {
  complete: 'Agent network diagnostics complete',
  caution: 'Agent network diagnostics caution',
  warning: 'Agent network diagnostics warning',
  failure: 'Agent network diagnostics failure',
  starting: 'Connect diagnostics starting on accept',
  skipped: 'Connect diagnostics skipped — Connect instance URL missing',
  threw: 'Connect diagnostics threw',
  finishedBreadcrumb: 'Connect diagnostics finished',
} as const

export const DIAGNOSTICS_MODULE = 'diagnostics'
export const DIAGNOSTICS_TRIGGER_ACCEPT = 'call-accept'

export type DiagnosticsIdentity = {
  userId?: string
  userEmail?: string
  agentName?: string
  callId?: string
  callHeaderId?: string | number
  contactId?: string
  trigger?: string
}

export type DiagnosticsLogAttributes = {
  module: typeof DIAGNOSTICS_MODULE
  trigger: string
  version: string
  environment: string
  userId: string
  userEmail: string
  agentName: string
  contactId: string
  callId: string
  callHeaderId: string
  connectInstanceUrl: string
  regions: string
  overallTier: LatencyTier
  latencyTier: LatencyTier
  mediaOk: boolean
  rtcSupport: boolean
  isSupportedBrowser: boolean
  hasMicrophone: boolean
  hasMicrophoneAccess: string
  hasSpeakers: boolean
  browserName: string
  osName: string
  instanceAvgLatency: number
  instanceMaxLatency: number
  instanceMinLatency: number
  regionAvgLatency: number
  regionMaxLatency: number
  regionMinLatency: number
  mediaTimedOutCount: number
  clientErrorCount: number
  durationMs: number
  online: boolean
  /** Compact JSON for detail — not the primary filter field. */
  latencyEndpointsJson: string
  mediaRegionsJson: string
}

export const messageForTier = (tier: LatencyTier): string => {
  switch (tier) {
    case 'failure':
      return DIAGNOSTICS_LOG_MESSAGES.failure
    case 'warning':
      return DIAGNOSTICS_LOG_MESSAGES.warning
    case 'caution':
      return DIAGNOSTICS_LOG_MESSAGES.caution
    default:
      return DIAGNOSTICS_LOG_MESSAGES.complete
  }
}

const asString = (value: unknown): string => {
  if (value == null || value === '') return ''
  return String(value)
}

export const summarizeLatencyEndpoint = (endpoint: LatencyEndpointResult) => ({
  name: endpoint.name,
  code: endpoint.code || undefined,
  endpoint: endpoint.endpoint,
  success: endpoint.success,
  tier: endpoint.tier,
  averageLatency: endpoint.averageLatency,
  minLatency: endpoint.minLatency,
  maxLatency: endpoint.maxLatency,
  iterations: endpoint.latencyItems.length,
  failedSamples: endpoint.latencyItems.filter((s) => !s.success).length,
})

export const summarizeMediaEndpoint = (endpoint: MediaEndpointResult) => ({
  endpoint: endpoint.endpoint,
  success: endpoint.success,
  timedOut: endpoint.timedOut,
  error: endpoint.error,
  candidateTypes: endpoint.candidates.map((c) => c.type),
  candidateCount: endpoint.candidates.length,
})

/**
 * Flatten diagnostics into primitive Sentry Logs attributes.
 * Nested objects are not reliably filterable in Logs — keep identifiers top-level.
 */
export const buildDiagnosticsLogAttributes = (
  report: ConnectDiagnosticsReport,
  identity: DiagnosticsIdentity = {}
): DiagnosticsLogAttributes => {
  const instance = report.latency.endpoints.find((e) => !e.code)
  const region = report.latency.endpoints.find((e) => e.code === (report.regions[0] || 'us-east-1'))
  const mediaEndpoints = report.media.regions.flatMap((r) => r.endpoints)

  return {
    module: DIAGNOSTICS_MODULE,
    trigger: identity.trigger || DIAGNOSTICS_TRIGGER_ACCEPT,
    version: report.version,
    environment: report.environment,
    userId: asString(identity.userId),
    userEmail: asString(identity.userEmail),
    agentName: asString(identity.agentName),
    contactId: asString(identity.contactId),
    callId: asString(identity.callId),
    callHeaderId: asString(identity.callHeaderId),
    connectInstanceUrl: report.connectInstanceUrl,
    regions: report.regions.join(','),
    overallTier: report.overallTier,
    latencyTier: report.latency.overallTier,
    mediaOk: report.media.success,
    rtcSupport: report.clientConfig.rtcSupport,
    isSupportedBrowser: report.clientConfig.isSupportedBrowser,
    hasMicrophone: report.clientConfig.hasMicrophone,
    hasMicrophoneAccess:
      report.clientConfig.hasMicrophoneAccess == null
        ? 'unknown'
        : String(report.clientConfig.hasMicrophoneAccess),
    hasSpeakers: report.clientConfig.hasSpeakers,
    browserName: report.clientConfig.browserName,
    osName: report.clientConfig.osName,
    instanceAvgLatency: instance?.averageLatency ?? -1,
    instanceMaxLatency: instance?.maxLatency ?? -1,
    instanceMinLatency: instance?.minLatency ?? -1,
    regionAvgLatency: region?.averageLatency ?? -1,
    regionMaxLatency: region?.maxLatency ?? -1,
    regionMinLatency: region?.minLatency ?? -1,
    mediaTimedOutCount: mediaEndpoints.filter((e) => e.timedOut).length,
    clientErrorCount: report.clientConfig.errorMessages.length,
    durationMs: report.endedAt - report.startedAt,
    online: report.online,
    latencyEndpointsJson: JSON.stringify(report.latency.endpoints.map(summarizeLatencyEndpoint)),
    mediaRegionsJson: JSON.stringify(
      report.media.regions.map((r) => ({
        code: r.code,
        name: r.name,
        success: r.success,
        endpoints: r.endpoints.map(summarizeMediaEndpoint),
      }))
    ),
  }
}
